import ExcelJS from 'exceljs'
import { CONTRACT_MODEL } from '@/helpers/constants'
import { getProductCharacteristicsForExcel } from '@/helpers/excel/productCharacteristics'
import { groupAdditionalServicesForExcel } from '@/helpers/excel/additionalServices'

// Colores corporativos CTT
const COLORS = {
  PRIMARY_RED: 'DFDF0024', // Rojo corporativo CTT #df0024 (ARGB format)
  WHITE: 'FFFFFFFF',
  LIGHT_GREY: 'FFF4F4F4',
  GREY_BORDER: 'FFE2E2E2',
  BLACK: 'FF000000',
}

const TOTAL_COLUMNS = 6

/**
 * Estilo de encabezado rojo corporativo
 */
const getHeaderStyle = () => ({
  fill: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: COLORS.PRIMARY_RED },
  },
  font: {
    bold: true,
    color: { argb: COLORS.WHITE },
    size: 12,
  },
  alignment: {
    horizontal: 'center',
    vertical: 'middle',
  },
  border: {
    top: { style: 'thin' },
    bottom: { style: 'thin' },
    left: { style: 'thin' },
    right: { style: 'thin' },
  },
})

/**
 * Estilo de título de sección
 */
const getSectionTitleStyle = () => ({
  fill: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: COLORS.PRIMARY_RED },
  },
  font: {
    bold: true,
    color: { argb: COLORS.WHITE },
    size: 14,
  },
  alignment: {
    horizontal: 'center',
    vertical: 'middle',
  },
})

/**
 * Estilo de subtítulo (nombres de productos)
 */
const getSubheaderStyle = () => ({
  fill: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: COLORS.LIGHT_GREY },
  },
  font: {
    bold: true,
    size: 11,
    color: { argb: COLORS.BLACK },
  },
  alignment: {
    horizontal: 'left',
    vertical: 'middle',
  },
})

/**
 * Estilo para etiquetas (columna izquierda)
 */
const getLabelStyle = () => ({
  font: {
    bold: true,
    size: 10,
  },
  alignment: {
    horizontal: 'left',
    vertical: 'middle',
  },
})

const getTextBodyStyle = () => ({
  font: {
    size: 10,
  },
  alignment: {
    horizontal: 'left',
    vertical: 'top',
    wrapText: true,
  },
})

const getAdditionalServiceGroupStyle = () => ({
  fill: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: COLORS.PRIMARY_RED },
  },
  font: {
    bold: true,
    size: 12,
    color: { argb: COLORS.WHITE },
  },
  alignment: {
    horizontal: 'left',
    vertical: 'middle',
  },
})

const mergeAndStyleRow = (worksheet, rowNumber, value, style, startColumn = 1, endColumn = TOTAL_COLUMNS) => {
  worksheet.mergeCells(rowNumber, startColumn, rowNumber, endColumn)
  const cell = worksheet.getCell(rowNumber, startColumn)
  cell.value = value
  const alignment = style.alignment || {}
  cell.style = {
    ...style,
    alignment: {
      ...alignment,
      wrapText: alignment.wrapText ?? true,
    },
  }
}

const formatAdditionalServiceValue = (service, t, lang) => {
  if (service?.value === null || service?.value === undefined) {
    return '-'
  }

  const numericValue = Number(service.value)

  if (!Number.isNaN(numericValue) && numericValue === 0) {
    return t(`EXCEL_NOT_INCLUDED_${lang}`)
  }

  if (Number.isNaN(numericValue)) {
    return `${service.value}${service.type === 'PERCENTAGE' ? '%' : ''}`
  }

  if (service.type === 'PERCENTAGE') {
    return `${numericValue.toFixed(2)}%`
  }

  return `${numericValue.toFixed(2)}€`
}

/**
 * Exporta la oferta a Excel con ExcelJS (soporte completo de estilos)
 * @param {Object} offer - Objeto de oferta completo
 * @param {Function} t - Función de traducción i18n
 */
export const exportOfferToExcel = async (offer, t) => {
  try {
    // Crear un nuevo workbook con ExcelJS
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet('Oferta')

    // Determinar el sufijo del idioma basado en el modelo de contrato
    const contractModel =
      offer?.rateModelDesc?.contractModel ||
      offer?.contractModel ||
      offer?.contract?.model ||
      CONTRACT_MODEL.PDF_VENTA_DIRECTA_2025_PT

    const isPortugal = contractModel === CONTRACT_MODEL.PDF_VENTA_DIRECTA_2025_PT || 
                       contractModel === CONTRACT_MODEL.PDF_VENTA_DIRECTA_2025_PT_NEXUS ||
                       contractModel === CONTRACT_MODEL.PDF_VENTA_DIRECTA_2026_PT_NEXUS
    const lang = isPortugal ? 'PT' : 'ES'

    let currentRow = 1

    // TÍTULO PRINCIPAL
    const titleCell = worksheet.getCell(`A${currentRow}`)
    titleCell.value = t(`EXCEL_TITLE_${lang}`)
    titleCell.style = getSectionTitleStyle()
    currentRow += 2 // Línea vacía

    // INFORMACIÓN GENERAL
    const infoCell = worksheet.getCell(`A${currentRow}`)
    infoCell.value = t(`EXCEL_INFO_GENERAL_${lang}`)
    infoCell.style = getSectionTitleStyle()
    currentRow++

    // Datos generales con estilo de etiqueta
    const generalInfo = [
      [t(`EXCEL_ID_OFFER_${lang}`), offer.offerId || ''],
      [t(`EXCEL_CLIENT_${lang}`), offer.clientSocialReason || ''],
      [t(`EXCEL_CIF_${lang}`), offer.clientCif || offer.client?.cif || ''],
      [t(`EXCEL_CITY_GENERAL_${lang}`), offer.client?.city || ''],
      [t(`EXCEL_ADDRESS_GENERAL_${lang}`), offer.client?.address || ''],
      [t(`EXCEL_LEGAL_CITY_${lang}`), offer.legalCity || ''],
      [t(`EXCEL_COMMITTED_DELIVERIES_${lang}`), `${offer.clientCommitedDeliveries || 0} / mes`],
      [t(`EXCEL_B2B_DIST_${lang}`), `${offer.b2b || 0}%`],
      [t(`EXCEL_B2C_DIST_${lang}`), `${offer.b2c || 0}%`],
      [t(`EXCEL_OFFER_TYPE_${lang}`), offer.type || ''],
      [t(`EXCEL_STATUS_${lang}`), offer.status || ''],
      [t(`EXCEL_VALIDATION_STATUS_${lang}`), offer.validationStatus || ''],
    ]

    generalInfo.forEach(([label, value]) => {
      const labelCell = worksheet.getCell(`A${currentRow}`)
      labelCell.value = label
      labelCell.style = getLabelStyle()
      
      const valueCell = worksheet.getCell(`B${currentRow}`)
      valueCell.value = value
      currentRow++
    })
    currentRow++ // Línea vacía

    // PRODUCTOS Y PRECIOS
    const productsTitleCell = worksheet.getCell(`A${currentRow}`)
    productsTitleCell.value = t(`EXCEL_PRODUCTS_PRICES_${lang}`)
    productsTitleCell.style = getSectionTitleStyle()
    currentRow += 2 // Línea vacía

    // Obtener productos seleccionados
    const selectedProducts = offer.productList?.filter(
      (p) => p.isMandatory || p.isPreSelected
    ) || []

    if (selectedProducts.length > 0) {
      selectedProducts.forEach((product) => {
        // Obtener el contractModel para buscar las características
        const contractModel =
          offer?.rateModelDesc?.contractModel ||
          offer?.contractModel ||
          offer?.contract?.model ||
          CONTRACT_MODEL.PDF_VENTA_DIRECTA_2025_PT

        const characteristics = getProductCharacteristicsForExcel(contractModel, product)
        
        // Usar el productLabel de las características o fallback al nombre traducido
        const translate = (key) => {
          if (!key) return null
          const translated = t(key)
          return translated && translated !== key ? translated : null
        }

        const productName = characteristics?.productLabel || 
          translate(product.desc_key) ||
          translate(`PRODUCT_${product.id}`) ||
          product.desc_key ||
          product.id
        
        // Fila "Producto:"
        const productLabelCell = worksheet.getCell(`A${currentRow}`)
        productLabelCell.value = t(`EXCEL_PRODUCT_LABEL_${lang}`)
        productLabelCell.style = getLabelStyle()
        
        const productNameCell = worksheet.getCell(`B${currentRow}`)
        productNameCell.value = productName
        productNameCell.style = getTextBodyStyle()
        currentRow++
        
        // Fila "Oferta:" - obtenida de las características del producto
        if (characteristics?.offerLabel) {
          const offerLabelCell = worksheet.getCell(`A${currentRow}`)
          offerLabelCell.value = t(`EXCEL_OFFER_LABEL_${lang}`)
          offerLabelCell.style = getLabelStyle()
          
          const offerValueCell = worksheet.getCell(`B${currentRow}`)
          offerValueCell.value = characteristics.offerLabel
          offerValueCell.style = getTextBodyStyle()
          currentRow++
        }
        
        currentRow++ // Línea vacía

        // Obtener solo las zonas de ESTE producto
        const productZones = product.zoneList || []
        
        // Obtener solo los pesos de ESTE producto
        const productWeights = (product.currentWeightIdList || []).sort((a, b) => a - b)

        if (productZones.length > 0 && productWeights.length > 0) {
          // Encabezados de tabla: Peso + cada zona del producto
          const headerRow = worksheet.getRow(currentRow)
          headerRow.getCell(1).value = t(`EXCEL_WEIGHT_${lang}`)
          headerRow.getCell(1).style = getHeaderStyle()
          
          productZones.forEach((zone, index) => {
            const cell = headerRow.getCell(index + 2)
            cell.value = t(zone.desc_key || `ZONE_${zone.id}`)
            cell.style = getHeaderStyle()
          })
          currentRow++

          // Filas de datos por cada peso
          productWeights.forEach((weight) => {
            const dataRow = worksheet.getRow(currentRow)
            dataRow.getCell(1).value = `${weight} kg`

            productZones.forEach((zone, index) => {
              const basePrice = zone.basePrices?.find((bp) => bp.id === weight)
              const cell = dataRow.getCell(index + 2)
              if (basePrice && basePrice.price !== undefined) {
                cell.value = basePrice.price
                cell.numFmt = '#,##0.00€'
              } else {
                cell.value = '-'
              }
            })
            currentRow++
          })

          // Fila Kg Adicional
          const addKgRow = worksheet.getRow(currentRow)
          const addKgCell = addKgRow.getCell(1)
          addKgCell.value = t(`EXCEL_ADDITIONAL_KG_${lang}`)
          addKgCell.style = getSubheaderStyle()

          productZones.forEach((zone, index) => {
            const cell = addKgRow.getCell(index + 2)
            cell.style = getSubheaderStyle()
            if (zone.additionalKgValue !== undefined && zone.additionalKgValue !== null) {
              cell.value = zone.additionalKgValue
              cell.numFmt = '#,##0.00€'
            } else {
              cell.value = '-'
            }
          })
          currentRow++
        }

        // Mostrar características del producto
        if (characteristics?.lines?.length) {
          currentRow++ // margen antes de las características

          characteristics.lines.forEach((line) => {
            // Parsear la línea para separar título y contenido
            // Formato esperado: "• Título: contenido" o "• Título contenido"
            const lineText = line.replace(/^•\s*/, '') // Eliminar el bullet point
            const colonIndex = lineText.indexOf(':')
            
            if (colonIndex > 0) {
              // Hay dos puntos, separar título y contenido
              const title = lineText.substring(0, colonIndex + 1) // Incluye los ":"
              const content = lineText.substring(colonIndex + 1).trim()
              
              const titleCell = worksheet.getCell(`A${currentRow}`)
              titleCell.value = title
              titleCell.style = getLabelStyle()
              
              const contentCell = worksheet.getCell(`B${currentRow}`)
              contentCell.value = content
              contentCell.style = getTextBodyStyle()
            } else {
              // No hay dos puntos, mostrar todo el texto en una celda merged
              mergeAndStyleRow(worksheet, currentRow, lineText, getTextBodyStyle())
            }
            
            currentRow++
          })

          currentRow++ // margen después de las características
        }

        currentRow += 3 // Separación adicional entre tablas de productos
      })
    } else {
      worksheet.getCell(`A${currentRow}`).value = t(`EXCEL_NO_PRODUCTS_${lang}`)
      currentRow += 2
    }

    // MÉTODOS DE PAGO
    const paymentTitleCell = worksheet.getCell(`A${currentRow}`)
    paymentTitleCell.value = t(`EXCEL_PAYMENT_METHODS_${lang}`)
    paymentTitleCell.style = getSectionTitleStyle()
    currentRow++
    
    const selectedPayment = offer.paymentMethods?.find((pm) => pm.isSelected)
    if (selectedPayment) {
      const paymentLabel = worksheet.getCell(`A${currentRow}`)
      paymentLabel.value = t(`EXCEL_SELECTED_METHOD_${lang}`)
      paymentLabel.style = getLabelStyle()
      worksheet.getCell(`B${currentRow}`).value = selectedPayment.id || ''
      currentRow++
      
      if (offer.additionalServices?.paymentDay) {
        const dayLabel = worksheet.getCell(`A${currentRow}`)
        dayLabel.value = t(`EXCEL_PAYMENT_DAY_${lang}`)
        dayLabel.style = getLabelStyle()
        worksheet.getCell(`B${currentRow}`).value = offer.additionalServices.paymentDay
        currentRow++
      }
    }
    currentRow++ // Línea vacía

    // SERVICIOS ADICIONALES
    const visibleServices = offer.extraServicesList?.filter((s) => s.isVisible) || []
    const groupedServices = groupAdditionalServicesForExcel(contractModel, visibleServices)

    if (groupedServices.length > 0) {
      const servicesTitle = worksheet.getCell(`A${currentRow}`)
      servicesTitle.value = t(`EXCEL_ADDITIONAL_SERVICES_${lang}`)
      servicesTitle.style = getSectionTitleStyle()
      currentRow++

      groupedServices.forEach((group, index) => {
        if (index > 0) {
          currentRow++
        }

        mergeAndStyleRow(worksheet, currentRow, group.title, getAdditionalServiceGroupStyle(), 1, 2)
        currentRow++

        group.services.forEach((service) => {
          const labelCell = worksheet.getCell(`A${currentRow}`)
          labelCell.value = t(service.desc_key || service.id)
          labelCell.style = getLabelStyle()

          const valueCell = worksheet.getCell(`B${currentRow}`)
          valueCell.value = formatAdditionalServiceValue(service, t, lang)
          currentRow++
        })

        currentRow += 2
      })
    }

    // PUNTOS DE SERVICIO
    if (offer.optyServicePoints && offer.optyServicePoints.length > 0) {
      const spTitle = worksheet.getCell(`A${currentRow}`)
      spTitle.value = t(`EXCEL_SERVICE_POINTS_${lang}`)
      spTitle.style = getSectionTitleStyle()
      currentRow++
      
      const spHeaderRow = worksheet.getRow(currentRow)
      spHeaderRow.getCell(1).value = t(`EXCEL_ADDRESS_${lang}`)
      spHeaderRow.getCell(1).style = getHeaderStyle()
      spHeaderRow.getCell(2).value = t(`EXCEL_CITY_${lang}`)
      spHeaderRow.getCell(2).style = getHeaderStyle()
      spHeaderRow.getCell(3).value = t(`EXCEL_COMMITTED_SHIPMENTS_${lang}`)
      spHeaderRow.getCell(3).style = getHeaderStyle()
      spHeaderRow.getCell(4).value = t(`EXCEL_FAVORITE_${lang}`)
      spHeaderRow.getCell(4).style = getHeaderStyle()
      currentRow++
      
      offer.optyServicePoints.forEach((sp) => {
        const spRow = worksheet.getRow(currentRow)
        spRow.getCell(1).value = sp.address1 || ''
        spRow.getCell(2).value = sp.cityName || ''
        spRow.getCell(3).value = sp.compromisedShipments || 0
        spRow.getCell(4).value = sp.isFavorite ? t(`EXCEL_YES_${lang}`) : t(`EXCEL_NO_${lang}`)
        currentRow++
      })
      currentRow++
    }

    // DATOS BANCARIOS
    if (offer.bankDetails) {
      const bankTitle = worksheet.getCell(`A${currentRow}`)
      bankTitle.value = t(`EXCEL_BANK_DETAILS_${lang}`)
      bankTitle.style = getSectionTitleStyle()
      currentRow++
      
      if (offer.bankDetails.accountIBAN) {
        const ibanLabel = worksheet.getCell(`A${currentRow}`)
        ibanLabel.value = t(`EXCEL_IBAN_${lang}`)
        ibanLabel.style = getLabelStyle()
        worksheet.getCell(`B${currentRow}`).value = offer.bankDetails.accountIBAN
        currentRow++
      }
      if (offer.bankDetails.iban2) {
        const iban2Label = worksheet.getCell(`A${currentRow}`)
        iban2Label.value = t(`EXCEL_IBAN2_${lang}`)
        iban2Label.style = getLabelStyle()
        worksheet.getCell(`B${currentRow}`).value = offer.bankDetails.iban2
        currentRow++
      }
      currentRow++
    }

    // GARANTÍA BANCARIA
    if (offer.bankGuarantee && offer.hasBankGuarantee) {
      const bgTitle = worksheet.getCell(`A${currentRow}`)
      bgTitle.value = t(`EXCEL_BANK_GUARANTEE_${lang}`)
      bgTitle.style = getSectionTitleStyle()
      currentRow++
      
      const amountLabel = worksheet.getCell(`A${currentRow}`)
      amountLabel.value = t(`EXCEL_AMOUNT_${lang}`)
      amountLabel.style = getLabelStyle()
      worksheet.getCell(`B${currentRow}`).value = offer.bankGuarantee.amount || ''
      currentRow++
      
      const selectedType = offer.bankGuarantee.types?.find((type) => type.isSelected)
      if (selectedType) {
        const typeLabel = worksheet.getCell(`A${currentRow}`)
        typeLabel.value = t(`EXCEL_TYPE_${lang}`)
        typeLabel.style = getLabelStyle()
        worksheet.getCell(`B${currentRow}`).value = selectedType.key
        currentRow++
      }
      
      const selectedDuration = offer.bankGuarantee.durations?.find((d) => d.isSelected)
      if (selectedDuration) {
        const durationLabel = worksheet.getCell(`A${currentRow}`)
        durationLabel.value = t(`EXCEL_DURATION_${lang}`)
        durationLabel.style = getLabelStyle()
        worksheet.getCell(`B${currentRow}`).value = selectedDuration.key
        currentRow++
      }
    }

    // Ajustar anchos de columna
    worksheet.getColumn(1).width = 60
    worksheet.getColumn(2).width = 20
    worksheet.getColumn(3).width = 20
    worksheet.getColumn(4).width = 20
    worksheet.getColumn(5).width = 20
    worksheet.getColumn(6).width = 20

    // Generar nombre de archivo
    const fileName = `Oferta_${offer.offerId || 'Sin_ID'}_${offer.clientSocialReason?.replace(/[^a-zA-Z0-9]/g, '_') || 'Cliente'}.xlsx`

    // Generar buffer y descargar
    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    
    // Crear link de descarga
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = fileName
    link.click()
    URL.revokeObjectURL(link.href)

    return true
  } catch (error) {
    console.error('Error exportando oferta a Excel:', error)
    return false
  }
}
